import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

// Topics to avoid
const AVOID_TOPICS = [
  { id: 'avoid_politics', labelKey: 'politics' },
  { id: 'avoid_religion', labelKey: 'religion' },
  { id: 'avoid_controversial', labelKey: 'controversial' },
  { id: 'avoid_competitors', labelKey: 'competitors' },
  { id: 'avoid_pricing', labelKey: 'pricing' },
  { id: 'avoid_health_claims', labelKey: 'healthClaims' },
  { id: 'avoid_greenwashing', labelKey: 'greenwashing' },
  { id: 'avoid_personal', labelKey: 'personal' },
  { id: 'avoid_current_events', labelKey: 'currentEvents' },
]

// Tones to avoid
const AVOID_TONES = [
  { id: 'tone_avoid_pushy', labelKey: 'pushy' },
  { id: 'tone_avoid_formal', labelKey: 'formal' },
  { id: 'tone_avoid_casual', labelKey: 'casual' },
  { id: 'tone_avoid_emoji_heavy', labelKey: 'emojiHeavy' },
  { id: 'tone_avoid_hashtag_heavy', labelKey: 'hashtagHeavy' },
  { id: 'tone_avoid_superlatives', labelKey: 'superlatives' },
  { id: 'tone_avoid_clickbait', labelKey: 'clickbait' },
]

/**
 * Multi-Select Chip Component
 */
function ChipSelect({ options, selected, onChange, translationPrefix, t }) {
  const toggleOption = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((item) => item !== id))
    } else {
      onChange([...selected, id])
    }
  }

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const isSelected = selected.includes(option.id)
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => toggleOption(option.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              isSelected
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t(`${translationPrefix}.${option.labelKey}`)}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Tag Input Component for forbidden words
 */
function TagInput({ tags, onAdd, onRemove, maxTags = 20, maxLength = 30, placeholder }) {
  const { t } = useTranslation('brandProfile')
  const [inputValue, setInputValue] = useState('')

  const handleAdd = (value) => {
    const trimmed = value.trim().toLowerCase()
    if (trimmed && tags.length < maxTags && trimmed.length <= maxLength && !tags.includes(trimmed)) {
      onAdd(trimmed)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      handleAdd(inputValue)
      setInputValue('')
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      // Remove last tag on backspace if input is empty
      onRemove(tags.length - 1)
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')
    const words = pastedText.split(/[,\n]+/).map((w) => w.trim()).filter(Boolean)
    words.forEach((word) => handleAdd(word))
    setInputValue('')
  }

  return (
    <div className="space-y-2">
      {/* Tags display */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-700"
            >
              {tag}
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="ml-2 text-red-500 hover:text-red-700"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input */}
      {tags.length < maxTags && (
        <div className="relative">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value.slice(0, maxLength))}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={placeholder}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
            {t('common.pressEnter')}
          </div>
        </div>
      )}

      {/* Counter */}
      <div className="text-xs text-gray-400">
        {tags.length}/{maxTags} {t('wizard.steps.content_restrictions.forbiddenWords.words')}
      </div>
    </div>
  )
}

/**
 * Restrictions Step Component
 *
 * Step 4 of the Brand Profile Wizard.
 * Collects: Topics to avoid, forbidden words, tones to avoid, additional restrictions
 */
export default function RestrictionsStep({ data, onChange }) {
  const { t } = useTranslation('brandProfile')

  // Handle avoid topics change
  const handleTopicsChange = useCallback(
    (avoid_topics) => {
      onChange({ avoid_topics })
    },
    [onChange]
  )

  // Handle forbidden word add
  const handleAddWord = useCallback(
    (word) => {
      onChange({ forbidden_words: [...data.forbidden_words, word] })
    },
    [onChange, data.forbidden_words]
  )

  // Handle forbidden word remove
  const handleRemoveWord = useCallback(
    (index) => {
      onChange({ forbidden_words: data.forbidden_words.filter((_, i) => i !== index) })
    },
    [onChange, data.forbidden_words]
  )

  // Handle avoid tones change
  const handleTonesChange = useCallback(
    (avoid_tones) => {
      onChange({ avoid_tones })
    },
    [onChange]
  )

  // Handle additional restrictions change
  const handleAdditionalChange = useCallback(
    (e) => {
      onChange({ additional_restrictions: e.target.value.slice(0, 300) })
    },
    [onChange]
  )

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          {t('wizard.steps.content_restrictions.title')}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('wizard.steps.content_restrictions.subtitle')}
        </p>
      </div>

      {/* Topics to Avoid */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {t('wizard.steps.content_restrictions.avoidTopics.label')}
        </label>
        <ChipSelect
          options={AVOID_TOPICS}
          selected={data.avoid_topics}
          onChange={handleTopicsChange}
          translationPrefix="wizard.steps.content_restrictions.avoidTopics.options"
          t={t}
        />
      </div>

      {/* Forbidden Words */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('wizard.steps.content_restrictions.forbiddenWords.label')}
        </label>
        <p className="text-xs text-gray-500 mb-3">
          {t('wizard.steps.content_restrictions.forbiddenWords.hint')}
        </p>
        <TagInput
          tags={data.forbidden_words}
          onAdd={handleAddWord}
          onRemove={handleRemoveWord}
          maxTags={20}
          placeholder={t('wizard.steps.content_restrictions.forbiddenWords.placeholder')}
        />
      </div>

      {/* Tones to Avoid */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {t('wizard.steps.content_restrictions.avoidTones.label')}
        </label>
        <ChipSelect
          options={AVOID_TONES}
          selected={data.avoid_tones}
          onChange={handleTonesChange}
          translationPrefix="wizard.steps.content_restrictions.avoidTones.options"
          t={t}
        />
      </div>

      {/* Additional Restrictions */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('wizard.steps.content_restrictions.additionalRestrictions.label')}
          <span className="ml-2 text-gray-400 font-normal">({t('common.optional')})</span>
        </label>
        <textarea
          value={data.additional_restrictions || ''}
          onChange={handleAdditionalChange}
          placeholder={t('wizard.steps.content_restrictions.additionalRestrictions.placeholder')}
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
        />
        <div className="mt-1 text-xs text-gray-400 text-right">
          {(data.additional_restrictions || '').length}/300
        </div>
      </div>

      {/* Warning Note */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-amber-500 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-amber-800">
              {t('wizard.steps.content_restrictions.warningTitle')}
            </p>
            <p className="mt-1 text-xs text-amber-700">
              {t('wizard.steps.content_restrictions.warningText')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
